<template>
    <div class="setting-page-content-wrapper company">
        <div class="page-header">
            <div class="title">{{ $t("message.company") }}</div>
            <div class="subtitle">{{ $t("settings.companySubtitles") }}</div>
        </div>
        <section>
            <div class="fields">
                <div class="field">
                    <label>{{ $t("message.companyName") }}</label>
                    <div class="input-wrapper">
                        <a-form-item :validate-status="errors.first('companyName') ? 'error' : 'success'" has-feedback.>
                            <a-input
                                :placeholder="$t('message.companyName')"
                                v-validate="'required'"
                                v-model="organizationName"
                                name="companyName"
                            />
                        </a-form-item>
                    </div>
                </div>
                <div class="field">
                    <label>{{ $t("message.companySlogan") }}</label>
                    <div class="input-wrapper">
                        <a-form-item
                            :validate-status="errors.first('companySlogan') ? 'error' : 'success'"
                            has-feedback.
                        >
                            <a-input
                                :placeholder="$t('message.companySlogan')"
                                v-validate="'required'"
                                v-model="companySlogan"
                                name="companyName"
                            />
                        </a-form-item>
                    </div>
                </div>
                <div class="field">
                    <label>{{ $t("chatbotv2.chatbotName") }}</label>
                    <div class="input-wrapper">
                        <a-form-item :validate-status="errors.first('chatbotName') ? 'error' : 'success'" has-feedback.>
                            <a-input
                                :placeholder="$t('chatbotv2.chatbotName')"
                                v-validate="'required'"
                                v-model="chatbotName"
                                name="chatbotName"
                            />
                        </a-form-item>
                    </div>
                </div>
                <div class="field">
                    <label>{{ $t("message.organizationLogo") }}</label>
                    <div class="input-wrapper">
                        <div
                            class="image-box"
                            :class="{ empty: !logoUrl }"
                            v-tippy="tippyOptions"
                            :content="$t('message.vtipyUploadLogo')"
                            @click="openSelectImage"
                        >
                            <EmptyImage class="empty" v-if="!logoUrl" />
                            <AddImageIcon class="upload-image" />
                            <img :src="uploading ? '/static/loader-animated.svg' : logoUrl" />
                        </div>
                    </div>
                </div>
                <div class="field">
                    <label>{{ $t("chatbotv2.chatbotLogo") }}</label>
                    <div class="input-wrapper">
                        <div
                            class="image-box"
                            :class="{ empty: !chatbotAvatarUrl }"
                            v-tippy="tippyOptions"
                            :content="$t('chatbotv2.chatbotLogo')"
                            @click="openSelectImageChatbotAvatar"
                        >
                            <EmptyImage class="empty" v-if="!chatbotAvatarUrl" />
                            <AddImageIcon class="upload-image" />
                            <img :src="chatbotUploading ? '/static/loader-animated.svg' : chatbotAvatarUrl" />
                        </div>
                    </div>
                </div>
                <div class="field">
                    <label>Site ID:</label>
                    <div class="input-wrapper">
                        <div @click="copy" style="display: flex; gap: 70px">
                            <label>{{ site._id }}</label>
                            <i class="material-icons">content_copy</i>
                        </div>
                    </div>
                </div>
                <div class="d-flex gap-5 field">
                    <div class="field" v-if="isSuperAdmin">
                        <label>Website Tracking URL</label>
                        <div class="input-wrapper">
                            <a-input v-model="websiteTrackingUrl" />
                        </div>
                    </div>
                    <div class="field" v-if="isSuperAdmin">
                        <label>Website Tracking ID</label>
                        <div class="input-wrapper">
                            <a-input v-model="websiteTrackingId" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { mapState } from "vuex";
import * as _ from "lodash";
import { DEFAULT_ORGANIZATION_LOGO_URL } from "../../../../../utils/staticDatas";
import { getExtensionFromFileName } from "../../../../../utils/utils";
import { uploadAsset } from "../../../../../api/assetUpload";
import mimeTypes from "../../../../../components/util/mimeTypes";
import axios from "axios";
import log2 from "../../../../../utils/log2";
import EmptyImage from "../../../../../components/svg/icons/EmptyImage.vue";
import AddImageIcon from "../../../../../components/svg/icons/AddImageIcon.vue";

const log = log2("Settings/Company");
export default {
    name: "Company",
    components: {
        EmptyImage,
        AddImageIcon,
    },
    props: {
        updateSettings: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            logoUrl: DEFAULT_ORGANIZATION_LOGO_URL,
            chatbotAvatarUrl: DEFAULT_ORGANIZATION_LOGO_URL,
            selectedImage: null,
            selectedImageSrc: null,
            uploading: false,
            chatbotUploading: false,
            tippyOptions: {
                followCursor: true,
                placement: "bottom-end",
            },
        };
    },

    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
            isSuperAdmin: (state) => !!state.auth.adminUser,
        }),
        organizationName: {
            get() {
                return this.settings.organizationName;
            },
            set(value) {
                this.updateSettings("organizationName", value);
            },
        },
        companySlogan: {
            get() {
                return this.settings.companySlogan;
            },
            set(value) {
                this.updateSettings("companySlogan", value);
            },
        },
        chatbotName: {
            get() {
                return this.settings.chatbotName;
            },
            set(value) {
                this.updateSettings("chatbotName", value);
            },
        },
        chatbotAvatar: {
            get() {
                return this.settings.chatbotAvatar;
            },
            set(value) {
                this.updateSettings("chatbotAvatar", value);
            },
        },
        organizationLogo: {
            get() {
                return this.settings.organizationLogo;
            },
            set(value) {
                this.updateSettings("organizationLogo", value);
            },
        },
        websiteTrackingUrl: {
            get() {
                return this.settings.websiteTrackingUrl;
            },
            set(value) {
                this.updateSettings("websiteTrackingUrl", value);
            },
        },
        websiteTrackingId: {
            get() {
                return this.settings.websiteTrackingId;
            },
            set(value) {
                this.updateSettings("websiteTrackingId", value);
            },
        },
    },
    methods: {
        openSelectImage() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute("accept", "image/*");
            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                if (file) {
                    const fileType = getExtensionFromFileName(file.name);
                    this.selectedImage = file;
                    this.selectedImageSrc = URL.createObjectURL(file);
                    this.uploading = true;
                    let path = `${this.site._id}/logo/`;

                    let { data } = await uploadAsset({
                        path,
                        mime: file.type,
                        name: file.name,
                        ext: mimeTypes[file.type] || fileType || "supsis",
                        size: file.size,
                        type: "other",
                    });
                    if (file.size <= 10 * 1024 * 1024) {
                        axios({
                            method: data.method,
                            url: data.url,
                            data: file,
                            headers: {
                                "Content-Type": file.type,
                            },
                        })
                            .catch((err) => {
                                this.$message.error({
                                    content: "Image upload failed",
                                    duration: 2,
                                    key: "uploading",
                                });
                            })
                            .finally(() => {
                                this.$message.success({
                                    content: `Image uploaded successfully`,
                                    duration: 2,
                                    key: "uploading",
                                });
                                this.uploading = false;
                                this.selectedImage = null;
                                this.selectedImageSrc = null;
                                this.organizationLogo = data.asset;
                                this.logoUrl = data.asset.v2Format && data.asset.cdnUrl
                                    ? data.asset.cdnUrl
                                    : `https://static.supsis.live/${this.organizationLogo.path}${this.organizationLogo.id}.${this.organizationLogo.ext}`;
                            });
                    } else {
                        log.warn("input is not suitable for send");
                        this.$message.error({
                            content: this.$t("message.assetOversize"),
                            duration: 2,
                            key: "uploading",
                        });
                        this.uploading = false;
                        this.selectedImage = null;
                        this.selectedImageSrc = null;
                    }
                } else {
                    this.selectedImage = null;
                    this.selectedImageSrc = null;
                    this.uploading = false;
                }
            };
            fileEl.click();
        },
        openSelectImageChatbotAvatar() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute("accept", "image/*");
            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                if (file) {
                    const fileType = getExtensionFromFileName(file.name);
                    this.selectedImage = file;
                    this.selectedImageSrc = URL.createObjectURL(file);
                    this.chatbotUploading = true;
                    let path = `${this.site._id}/logo/`;

                    let { data } = await uploadAsset({
                        path,
                        mime: file.type,
                        name: file.name,
                        ext: mimeTypes[file.type] || fileType || "supsis",
                        size: file.size,
                        type: "other",
                    });
                    if (file.size <= 10 * 1024 * 1024) {
                        axios({
                            method: data.method,
                            url: data.url,
                            data: file,
                            headers: {
                                "Content-Type": file.type,
                            },
                        })
                            .catch((err) => {
                                this.$message.error({
                                    content: "Image upload failed",
                                    duration: 2,
                                    key: "chatbotUploading",
                                });
                            })
                            .finally(() => {
                                this.$message.success({
                                    content: `Image uploaded successfully`,
                                    duration: 2,
                                    key: "chatbotUploading",
                                });
                                this.chatbotUploading = false;
                                this.selectedImage = null;
                                this.selectedImageSrc = null;
                                this.chatbotAvatar = data.asset;
                                this.chatbotAvatarUrl = data.asset.v2Format && data.asset.cdnUrl
                                    ? data.asset.cdnUrl
                                    : `https://static.supsis.live/${this.chatbotAvatar.path}${this.chatbotAvatar.id}.${this.chatbotAvatar.ext}`;
                            });
                    } else {
                        log.warn("input is not suitable for send");
                        this.$message.error({
                            content: this.$t("message.assetOversize"),
                            duration: 2,
                            key: "chatbotUploading",
                        });
                        this.chatbotUploading = false;
                        this.selectedImage = null;
                        this.selectedImageSrc = null;
                    }
                } else {
                    this.selectedImage = null;
                    this.selectedImageSrc = null;
                    this.chatbotUploading = false;
                }
            };
            fileEl.click();
        },
        copy() {
            navigator.clipboard.writeText(this.site._id).then(() => {
                this.$message.success("Site ID Kopyalandı");
            }).catch((err) => {
                this.$message.success("Site ID  Kopyalanamadı");
            });
        },
    },
    mounted() {
        if (this.settings.organizationLogo) {
            this.logoUrl = this.organizationLogo.v2Format && this.organizationLogo.cdnUrl
                ? this.organizationLogo.cdnUrl
                : `https://static.supsis.live/${this.organizationLogo.path}${this.organizationLogo.id}.${this.organizationLogo.ext}`;
        }
        if (this.settings.chatbotAvatar) {
            this.chatbotAvatarUrl = this.chatbotAvatar.v2Format && this.chatbotAvatar.cdnUrl
                ? this.chatbotAvatar.cdnUrl
                : `https://static.supsis.live/${this.chatbotAvatar.path}${this.chatbotAvatar.id}.${this.chatbotAvatar.ext}`;
        }
    },
    watch: {
        settings: {
            handler(newSettings, oldSettings, oldChatbotAvatarImage, newChatbotAvatarImage) {
                log("settings change watch", { newSettings });
                const oldLogo = _.get(oldSettings, "organizationLogo");
                const newLogo = _.get(newSettings, "organizationLogo");
                if (!!newLogo && !_.isEqual(newLogo, oldLogo)) {
                    this.logoUrl = newLogo.v2Format && newLogo.cdnUrl
                        ? newLogo.cdnUrl
                        : `https://static.supsis.live/${newLogo.path}${newLogo.id}.${newLogo.ext}`;
                }
                const oldChatbotAvatar = _.get(oldChatbotAvatarImage, "chatbotAvatar");
                const newChatbotAvatar = _.get(newChatbotAvatarImage, "chatbotAvatar");
                if (!!newChatbotAvatar && !_.isEqual(newChatbotAvatar, oldChatbotAvatar)) {
                    this.chatbotAvatarUrl = newChatbotAvatar.v2Format && newChatbotAvatar.cdnUrl
                        ? newChatbotAvatar.cdnUrl
                        : `https://static.supsis.live/${newChatbotAvatar.path}${newChatbotAvatar.id}.${newChatbotAvatar.ext}`;
                }
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
.image-box {
    width: 75px;
    height: 75px;
    border: 1px solid var(--color-border-default);
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;

    &.empty {
        svg.empty {
            width: 40px;
            height: 40px;
            fill: var(--color-border-default);
        }
    }

    .upload-image {
        display: none;
    }

    &:hover {
        cursor: pointer;
        background: var(--color-bg-hover);

        .upload-image {
            display: flex;
            width: 40px;
            height: 40px;
        }

        img {
            display: none;
        }

        &.empty {
            svg.empty {
                display: none;
            }
        }
    }
}
.page-header {
    justify-content: start !important;
}
</style>
